import type { RequestCompletion, RequestCompletionPart } from '@nest-recruit/shared';
import { FIELD_ALIASES } from './requisition-form-values';

// Request a hire as one card with tabs (owner, 2026-11-01, DECISIONS-LOG "Request a hire as tabs"). A tab shows a
// red count of what it still needs before sending, and turns green once that is filled. A tab with nothing needed says
// "optional" and turns green once everything on it is filled in. Kept out of the page so each rule is tested on its own.
// ADR 0083 (owner 2026-11-01 Q5 a): the 30-day and 90-day plans are optional; like the Ideal candidate tab, the plans' tab is
// green only once every required row of both templates is filled and every minimum met.
// Evening review 2026-11-01 (items 1 and 2): About the job comes straight after The request, and the two plans are one
// "Onboarding plan" tab after it, because "30-day plan" read like a 30-day hiring timeline.

export const REQUEST_TABS = ['position', 'request', 'job', 'onboarding', 'role', 'ideal'] as const;
export type RequestTab = (typeof REQUEST_TABS)[number];

export const REQUEST_TAB_LABELS: Readonly<Record<RequestTab, string>> = {
  position: 'Position',
  request: 'The request',
  job: 'About the job',
  onboarding: 'Onboarding plan',
  role: 'The role',
  ideal: 'Ideal candidate',
};

export const REQUEST_TAB_DESCRIPTIONS: Readonly<Record<RequestTab, string>> = {
  position: 'Which seat this hire is for.',
  request: 'How many people, how soon, and the budget.',
  job: "Optional. What goes into the job when it's approved. HR often rewrites it, and anything can change later in the job.",
  onboarding:
    'Optional now. What the new hire does in their first 30 and 90 days after they join, not how long hiring takes. The line manager finishes it for each hire before Day 1.',
  role: 'Why the team needs this person, and what they will do.',
  ideal: 'Optional, but the better this is, the better the ranking. It becomes Jev’s screening criteria.',
};

/** The tab each line of "How complete" opens. */
export const PART_TABS: Readonly<Record<RequestCompletionPart, RequestTab>> = {
  place: 'position',
  start: 'request',
  budget: 'request',
  why: 'role',
  responsibilities: 'role',
  achieve: 'role',
  plan_30: 'onboarding',
  plan_90: 'onboarding',
  idealCandidate: 'ideal',
  location: 'job',
  description: 'job',
  requirements: 'job',
};

/** The tab a form field sits on, by its top-level name. Anything not listed is on About the job. */
const FIELD_TABS: Readonly<Record<string, RequestTab>> = {
  place: 'position',
  hireKind: 'position',
  replacesStaffId: 'position',
  slotsRequested: 'request',
  priority: 'request',
  targetStartDate: 'request',
  targetStartLatest: 'request',
  budget: 'request',
  budgetMax: 'request',
  budgetCurrency: 'request',
  jevBudgetMicroUsd: 'request',
  responsibilities: 'role',
  brief: 'role',
  idealCandidate: 'ideal',
  idealProfile: 'ideal',
  managesPeople: 'position',
  plan30: 'onboarding',
  plan90: 'onboarding',
};

/** Choosing the position is one thing to fix, whether it is a seat or a new title in a department. */
const PLACE_FIELDS: readonly string[] = ['mode', 'positionId', 'ventureId', 'departmentId', 'title'];

/** One name per thing to fix: the send check's `justification` and the form's `brief.why` are the same box. */
function problemKey(name: string): string {
  const top = (FIELD_ALIASES[name] ?? name).split('.')[0] ?? name;
  return PLACE_FIELDS.includes(top) ? 'place' : top;
}

export function tabOfField(name: string): RequestTab {
  return FIELD_TABS[problemKey(name)] ?? 'job';
}

export interface RequestTabState {
  /** Needed to send, or refused, and not yet put right. */
  readonly toFix: number;
  /** Nothing on the tab is needed to send. */
  readonly isOptional: boolean;
  readonly isDone: boolean;
}

/**
 * `problems` are the field names still missing for sending plus those showing an error, in either the form's or the
 * server's naming.
 */
export function requestTabStates(completion: RequestCompletion, problems: readonly string[]): Readonly<Record<RequestTab, RequestTabState>> {
  const keys = new Set(problems.map(problemKey));
  const entries = REQUEST_TABS.map((tab): [RequestTab, RequestTabState] => {
    const items = completion.items.filter((item) => PART_TABS[item.part] === tab);
    const isOptional = !items.some((item) => item.isRequired);
    const toFix = [...keys].filter((key) => (FIELD_TABS[key] ?? 'job') === tab).length;
    const isDone = toFix === 0 && (!isOptional || items.every((item) => item.share >= 1));
    return [tab, { toFix, isOptional, isDone }];
  });
  return Object.fromEntries(entries) as Record<RequestTab, RequestTabState>;
}

/** The first tab, in order, that holds any of these fields: where Send takes the requester when something is missing. */
export function firstTabOf(fields: readonly string[]): RequestTab | null {
  return REQUEST_TABS.find((tab) => fields.some((field) => tabOfField(field) === tab)) ?? null;
}
